/**
 * Слой стен на холсте.
 *
 * Стены рисуются в два прохода. Первый — контур с обводкой вдвое толще нужной,
 * он же ловит клики. Второй — те же контуры одной заливкой: соседние стены —
 * отдельные многоугольники, и по их общим рёбрам (диагонали митр, торцы в
 * Т-стыках) проходила бы обводка. Повторная заливка закрывает её изнутри:
 * снаружи остаётся ровно половина обводки, а внутри стены сливаются в одно тело.
 * Контур выделенной стены — третьим проходом, иначе его накрыла бы заливка.
 *
 * Что делать по клику (поставить проём, объект, начать стену, выделить) — решает
 * холст: слой отдаёт ему событие вместе со стеной.
 */
import type { PointerEvent } from "react";
import type { WallOutline } from "../../plan";
import { BEARING_HATCH } from "@/shared/catalog";
import type { Wall } from "@/shared/plan";
import { WallMaterialHatch, WallShape } from "@/widgets/catalog";
import { planStageAllows } from "@/shared/store";
import { SelectionKind, Tool, useEditorStore } from "@/widgets/editor";

const WALL_FILL = "#39414f";
const SELECTED_WALL_FILL = "#e9f0fe";
const DEMOLISHED_WALL_FILL = "#fef2f2";
const DEMOLISHED_WALL_STROKE = "#dc2626";
const MOUNTED_WALL_FILL = "#ecfdf5";
const MOUNTED_WALL_STROKE = "#059669";

interface Props {
	walls: Wall[];
	outlines: Record<string, WallOutline>;
	/** Снесённые стены для пунктирного ориентира на этапе «Монтаж» */
	demolishedGhostOutlines?: Record<string, WallOutline>;
	mmPerPx: number;
	onWallPointerDown: (e: PointerEvent<Element>, wall: Wall) => void;
}

export const WallsLayer2D = ({
	walls,
	outlines,
	demolishedGhostOutlines,
	mmPerPx,
	onWallPointerDown,
}: Props) => {
	const selection = useEditorStore((s) => s.selection);
	const tool = useEditorStore((s) => s.tool);
	const planStage = useEditorStore((s) => s.planStage);
	const selectedId = selection?.kind === SelectionKind.Wall ? selection.id : null;
	const showDemolishedStyle = (wall: Wall) =>
		planStage === "demolition" && Boolean(wall.demolished);
	const showMountedStyle = (wall: Wall) => planStage === "construction" && Boolean(wall.mounted);

	const baseFill = (wall: Wall) => {
		if (wall.id === selectedId) return SELECTED_WALL_FILL;
		if (showDemolishedStyle(wall)) return DEMOLISHED_WALL_FILL;
		if (showMountedStyle(wall)) return MOUNTED_WALL_FILL;
		return wall.bearing ? BEARING_HATCH.stroke : WALL_FILL;
	};

	const baseStroke = (wall: Wall) => {
		if (showDemolishedStyle(wall)) return DEMOLISHED_WALL_STROKE;
		if (showMountedStyle(wall)) return MOUNTED_WALL_STROKE;
		return "#2b3440";
	};

	return (
		<>
			{/* Пунктирный след демонтированных стен на плане монтажа */}
			{demolishedGhostOutlines && (
				<g style={{ pointerEvents: "none" }}>
					{Object.entries(demolishedGhostOutlines).map(([id, outline]) => (
						<polygon
							key={`ghost-demolished-${id}`}
							points={outline.map((p) => `${p.x},${p.y}`).join(" ")}
							fill="#fef2f2"
							fillOpacity={0.65}
							stroke={DEMOLISHED_WALL_STROKE}
							strokeWidth={1.2 * mmPerPx}
							strokeDasharray={`${5 * mmPerPx} ${4 * mmPerPx}`}
							strokeLinejoin="miter"
						/>
					))}
				</g>
			)}

			<g>
				{walls.map((wall) => {
					const outline = outlines[wall.id];
					if (!outline) return null;
					return (
						<WallShape
							key={wall.id}
							id={wall.id}
							outline={outline}
							fill={baseFill(wall)}
							stroke={baseStroke(wall)}
							strokeWidth={1.8 * mmPerPx}
							cursor={
								planStage === "demolition"
									? "pointer"
									: // На этапах объектов стену не двигают — курсор не обещает этого
										!planStageAllows(planStage, "walls")
										? "default"
										: tool === Tool.Select
											? "move"
											: "pointer"
							}
							onPointerDown={(e) => onWallPointerDown(e, wall)}
						/>
					);
				})}
			</g>

			<g style={{ pointerEvents: "none" }}>
				{walls.map((wall) => {
					const outline = outlines[wall.id];
					if (!outline) return null;
					return (
						<polygon
							key={`fill-${wall.id}`}
							points={outline.map((p) => `${p.x},${p.y}`).join(" ")}
							fill={baseFill(wall)}
						/>
					);
				})}
			</g>

			<g style={{ pointerEvents: "none" }}>
				{walls.map((wall) => {
					const outline = outlines[wall.id];
					if (!outline) return null;
					if (showDemolishedStyle(wall)) {
						return (
							<polygon
								key={`demolish-outline-${wall.id}`}
								points={outline.map((p) => `${p.x},${p.y}`).join(" ")}
								fill="none"
								stroke={DEMOLISHED_WALL_STROKE}
								strokeWidth={1.8 * mmPerPx}
								strokeDasharray={`${6 * mmPerPx} ${4 * mmPerPx}`}
								strokeLinejoin="miter"
							/>
						);
					}
					return (
						<g key={`hatch-${wall.id}`}>
							<WallMaterialHatch wall={wall} outline={outline} />
							{showMountedStyle(wall) && (
								<polygon
									points={outline.map((p) => `${p.x},${p.y}`).join(" ")}
									fill="none"
									stroke={MOUNTED_WALL_STROKE}
									strokeWidth={1.8 * mmPerPx}
									strokeLinejoin="miter"
								/>
							)}
						</g>
					);
				})}
			</g>

			{selectedId && outlines[selectedId] && (
				<polygon
					points={outlines[selectedId].map((p) => `${p.x},${p.y}`).join(" ")}
					fill="none"
					stroke="#2f6feb"
					strokeWidth={1.4 * mmPerPx}
					strokeLinejoin="miter"
					style={{ pointerEvents: "none" }}
				/>
			)}
		</>
	);
};
